// Service Worker Registration for Push Notifications
// This enables web push notifications on mobile devices

interface ServiceWorkerRegistrationOptions {
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onError?: (error: Error) => void;
}

class ServiceWorkerRegistrationManager {
  private registration: ServiceWorkerRegistration | null = null;
  private isSupported: boolean;

  constructor() {
    this.isSupported =
      typeof window !== 'undefined' && 'serviceWorker' in navigator;
  }

  /**
   * Check if service workers are supported
   */
  isServiceWorkerSupported(): boolean {
    return this.isSupported;
  }

  /**
   * Register the service worker
   */
  async register(
    options: ServiceWorkerRegistrationOptions = {}
  ): Promise<ServiceWorkerRegistration | null> {
    if (!this.isSupported) {
      console.warn(
        '[Service Worker] Service workers are not supported in this browser'
      );
      options.onError?.(new Error('Service workers are not supported'));
      return null;
    }

    try {
      this.registration = await navigator.serviceWorker.register(
        '/service-worker.js',
        {
          scope: '/',
          updateViaCache: 'none',
        }
      );

      // Handle service worker updates
      this.registration.addEventListener('updatefound', () => {
        const newWorker = this.registration!.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (
              newWorker.state === 'installed' &&
              navigator.serviceWorker.controller
            ) {
              options.onUpdate?.(this.registration!);
            }
          });
        }
      });

      options.onSuccess?.(this.registration);
      return this.registration;
    } catch (error) {
      console.error('[Service Worker] Registration failed:', error);
      options.onError?.(error as Error);
      return null;
    }
  }

  /**
   * Get the current service worker registration
   */
  getRegistration(): ServiceWorkerRegistration | null {
    return this.registration;
  }

  /**
   * Unregister the service worker
   */
  async unregister(): Promise<boolean> {
    if (!this.registration) {
      return false;
    }

    try {
      const result = await this.registration.unregister();
      this.registration = null;
      return result;
    } catch (error) {
      console.error('[Service Worker] Unregistration failed:', error);
      return false;
    }
  }

  /**
   * Update the service worker
   */
  async update(): Promise<void> {
    if (!this.registration) {
      return;
    }

    try {
      await this.registration.update();
    } catch (error) {
      console.error('[Service Worker] Update failed:', error);
    }
  }

  /**
   * Skip waiting for the service worker (force activation of new version)
   */
  skipWaiting(): void {
    if (this.registration && this.registration.waiting) {
      this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  }

  /**
   * Check if the app is running as a standalone PWA
   */
  isStandalone(): boolean {
    if (typeof window === 'undefined') return false;

    // Extend Navigator interface for iOS standalone property
    interface NavigatorWithStandalone extends Navigator {
      standalone?: boolean;
    }

    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as NavigatorWithStandalone).standalone === true ||
      document.referrer.includes('android-app://')
    );
  }

  /**
   * Check if the device is mobile
   */
  isMobile(): boolean {
    if (typeof window === 'undefined') return false;

    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }

  /**
   * Check if the device is iOS
   */
  isIOS(): boolean {
    if (typeof window === 'undefined') return false;

    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  /**
   * Get mobile-specific notification requirements
   */
  getMobileRequirements(): {
    isMobile: boolean;
    isIOS: boolean;
    isStandalone: boolean;
    requiresHomeScreen: boolean;
    requirements: string[];
  } {
    const isMobile = this.isMobile();
    const isIOS = this.isIOS();
    const isStandalone = this.isStandalone();

    const requirements: string[] = [];

    if (isIOS && !isStandalone) {
      requirements.push('Add to Home Screen for iOS notifications');
    }

    if (isMobile) {
      requirements.push('Enable notifications in device settings');
    }

    return {
      isMobile,
      isIOS,
      isStandalone,
      requiresHomeScreen: isIOS && !isStandalone,
      requirements,
    };
  }
}

// Create singleton instance
const serviceWorkerManager = new ServiceWorkerRegistrationManager();

export default serviceWorkerManager;
